import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { slugify, type BrandDocument } from "@endeks/shared";
import { useBrand, useCreateBrand, useUpdateBrand, useProductGroups } from "./queries";
import { EntityTabs, type EntityTab } from "../../components/Editor/EntityTabs";
import { ProductGroupSelect } from "../../components/Common/ProductGroupSelect";
import { LangToggle, type Lang } from "../../components/Editor/LangToggle";
import MediaPicker from "../../components/Media/MediaPicker";
import BrandDocumentsField from "./BrandDocumentsField";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import {
  checkRequired,
  isBlank,
  type RequiredField,
  type FieldTarget,
} from "@/lib/form-validation";
import { errorMessage } from "@/lib/error-message";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { EN_ENTITY_BASES } from "@/config/site-routes";

export default function BrandForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: brand, isLoading } = useBrand(id ?? "");
  const { data: productGroupDict = [] } = useProductGroups();
  const createMutation = useCreateBrand();
  const updateMutation = useUpdateBrand();

  const [lang, setLang] = useState<Lang>("tr");
  const [tab, setTab] = useState<EntityTab>("content");
  const [mediaOpen, setMediaOpen] = useState(false);

  // Form state
  const [documents, setDocuments] = useState<BrandDocument[]>([]);
  const [name, setName] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEn, setSlugEn] = useState("");
  // EN slug elle düzenlenmediyse EN marka adından otomatik önerilir
  // Yeni kayıtta slug, kullanıcı elle düzenleyene kadar başlığı takip eder.
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugEnTouched, setSlugEnTouched] = useState(false);
  const [initial, setInitial] = useState("");
  const [productGroups, setProductGroups] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionEn, setDescriptionEn] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [featured, setFeatured] = useState(false);
  const [order, setOrder] = useState(0);

  // Kaydedilmemiş değişiklik takibi: yüklenen ilk değerlerle karşılaştırılır.
  const [loaded, setLoaded] = useState(!isEdit);
  const [baseline, setBaseline] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const snapshot = JSON.stringify({
    name,
    nameEn,
    slug,
    slugEn,
    initial,
    productGroups,
    description,
    descriptionEn,
    logoUrl,
    featured,
    order,
    documents,
  });
  // İlk değerler yüklendiği render'da taban alınır (effect yerine render sırasında)
  if (loaded && baseline === null) setBaseline(snapshot);

  // Populate on edit
  useEffect(() => {
    if (brand) {
      setName(brand.name ?? "");
      setNameEn(brand.nameEn ?? "");
      setSlug(brand.slug ?? "");
      // EN slug boşsa EN başlıktan önerilir. Eski EN adresi (TR slug) web'de yeni
      // adrese yönlendiği için link kırılmaz; dolu bir EN slug ise asla değiştirilmez.
      setSlugEn(brand.slugEn || (brand.nameEn ? slugify(brand.nameEn) : ""));
      setSlugEnTouched(!!brand.slugEn);
      setInitial(brand.initial ?? "");
      // Kayıtta ürün grubu boşsa (eski kayıtlar) kategoriler alanına düş
      setProductGroups(brand.productGroups || brand.categories || "");
      setDescription(brand.description ?? "");
      setDescriptionEn(brand.descriptionEn ?? "");
      setLogoUrl(brand.logoUrl ?? "");
      setFeatured(brand.featured ?? false);
      setOrder(brand.order ?? 0);
      setDocuments(brand.documents ?? []);
      setLoaded(true);
      setBaseline(null);
    }
  }, [brand]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const docFields = documents.flatMap((doc, i): RequiredField<FieldTarget<EntityTab>>[] => [
      {
        label: `Belge ${i + 1} başlığı`,
        missing: isBlank(doc.title),
        target: { tab: "media", id: `doc-title-${i}` },
      },
      {
        label: `Belge ${i + 1} dosyası`,
        missing: isBlank(doc.url),
        target: { tab: "media", id: `doc-title-${i}` },
      },
    ]);
    const valid = checkRequired<EntityTab>(
      [
        {
          label: "Marka Adı",
          missing: isBlank(name),
          target: { tab: "content", lang: "tr", id: "name" },
        },
        {
          label: "Slug",
          missing: isBlank(slug),
          target: { tab: "content", lang: "tr", id: "slug" },
        },
        {
          label: "Başlangıç Harfi",
          missing: isBlank(initial),
          target: { tab: "content", id: "initial" },
        },
        {
          label: "Ürün Grupları",
          missing: isBlank(productGroups),
          target: { tab: "content", id: "productGroups" },
        },
        ...docFields,
      ],
      (t) => {
        if (t.tab) setTab(t.tab);
        if (t.lang) setLang(t.lang);
      },
    );
    if (!valid) return;
    try {
      // EN karşılıkları TR↔EN sözlüğünden türetilir; sözlükte olmayan
      // (eski/serbest) adlar için TR adı korunur.
      const dict = new Map(
        productGroupDict.map((g) => [g.nameTr.toLocaleLowerCase("tr"), g.nameEn]),
      );
      const trList = productGroups
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const enJoined = trList.map((t) => dict.get(t.toLocaleLowerCase("tr")) || t).join(",");

      const payload = {
        name,
        nameEn: nameEn || null,
        slug,
        slugEn: slugEn || null,
        initial,
        // "Kategoriler" aslında ürün grupları — aynı değerle eşitlenir
        categories: productGroups,
        categoriesEn: enJoined || null,
        productGroups,
        productGroupsEn: enJoined || null,
        description,
        descriptionEn: descriptionEn || null,
        logoUrl,
        featured,
        order,
        documents,
      };

      if (isEdit) {
        await updateMutation.mutateAsync({ id: id!, ...payload });
        toast.success("Marka güncellendi");
      } else {
        await createMutation.mutateAsync(payload);
        toast.success("Marka oluşturuldu");
      }
      setSaved(true);
      navigate("/brands");
    } catch (error: unknown) {
      toast.error(isAxiosError(error) ? errorMessage(error, "Bir hata oluştu") : "Bir hata oluştu");
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const dirty = baseline !== null && snapshot !== baseline && !saved;
  const { confirmNavigate, dialog: unsavedDialog } = useUnsavedChanges(dirty);
  const seoPath = `/markalarimiz/${slug || ""}`;
  // SEO kaydı TR yolunda tutulur; önizleme URL'i seçili dilin yolunu ve slug'ını
  // gösterir (EN: İngilizce segment + EN slug, yoksa TR slug; "/en" öneki GooglePreview'da)
  const previewPath = lang === "en" ? `${EN_ENTITY_BASES.brand}/${slugEn || slug || ""}` : seoPath;

  if (isEdit && isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const contentPanel = (
    <form id="brand-form" noValidate onSubmit={handleSubmit} className="space-y-5 pt-4">
      {/* Lang toggle */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Dil:</span>
        <LangToggle value={lang} onChange={setLang} />
      </div>

      {/* name */}
      {lang === "tr" ? (
        <div className="space-y-1.5">
          <Label htmlFor="name">
            Marka Adı <span className="text-destructive">*</span>
          </Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!isEdit && !slugTouched) setSlug(slugify(e.target.value));
            }}
            required
          />
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="nameEn">Marka Adı (English)</Label>
          <Input
            id="nameEn"
            value={nameEn}
            onChange={(e) => {
              setNameEn(e.target.value);
              if (!slugEnTouched) setSlugEn(slugify(e.target.value));
            }}
          />
        </div>
      )}

      {/* slug */}
      {lang === "tr" ? (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="slug">
              Slug <span className="text-destructive">*</span>
            </Label>
            <button
              type="button"
              className="text-xs text-primary hover:underline disabled:opacity-50"
              disabled={!name.trim()}
              onClick={() => {
                setSlug(slugify(name));
                setSlugTouched(true);
              }}
              title={isEdit ? "Dikkat: yayındaki yazının adresi değişir" : undefined}
            >
              Başlıktan oluştur
            </button>
          </div>
          <Input
            id="slug"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugTouched(true);
            }}
            // Kullanıcı slug'ı elle değiştirdiyse (ya da yeni kayıtsa) biçimi düzelt;
            // dokunulmamış mevcut slug'a dokunma (canlı URL değişmesin).
            onBlur={() => (!isEdit || slugTouched) && setSlug(slugify(slug))}
            placeholder="marka-adi"
            required
          />
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="slugEn">Slug (EN)</Label>
          <Input
            id="slugEn"
            value={slugEn}
            onChange={(e) => {
              setSlugEn(e.target.value);
              setSlugEnTouched(true);
            }}
            onBlur={() => setSlugEn(slugify(slugEn))}
            placeholder={slug || "brand-name"}
          />
          <p className="text-xs text-muted-foreground">
            Boş bırakılırsa TR slug kullanılır{slug ? ` (${slug})` : ""}.
          </p>
        </div>
      )}

      {/* initial (always visible) */}
      <div className="space-y-1.5">
        <Label htmlFor="initial">
          Başlangıç Harfi <span className="text-destructive">*</span>
        </Label>
        <Input
          id="initial"
          value={initial}
          onChange={(e) => setInitial(e.target.value)}
          maxLength={3}
          required
          className="w-24"
        />
      </div>

      {/* Ürün grupları — TR↔EN sözlükten çoklu seçim; EN karşılıkları otomatik */}
      <div id="productGroups" className="space-y-1.5">
        <Label>
          Ürün Grupları <span className="text-destructive">*</span>
        </Label>
        <ProductGroupSelect value={productGroups} onChange={setProductGroups} lang={lang} />
        <p className="text-xs text-muted-foreground">
          {lang === "tr"
            ? 'Türkçe seçim yapın — İngilizce karşılıklar sözlükten otomatik eşlenir; çevirileri "Grupları Yönet"ten düzenleyebilirsiniz.'
            : "Seçim TR sekmesiyle ortaktır; burada İngilizce karşılıklarıyla görünür."}
        </p>
      </div>

      {/* description */}
      {lang === "tr" ? (
        <div className="space-y-1.5">
          <Label htmlFor="description">Açıklama</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="min-h-[160px]"
          />
          <p className="text-xs text-muted-foreground">Paragrafları boş satırla ayırın</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="descriptionEn">Açıklama (English)</Label>
          <Textarea
            id="descriptionEn"
            value={descriptionEn}
            onChange={(e) => setDescriptionEn(e.target.value)}
            className="min-h-[160px]"
          />
          <p className="text-xs text-muted-foreground">Separate paragraphs with a blank line</p>
        </div>
      )}

      {/* featured */}
      <div className="flex items-center gap-3">
        <Switch id="featured" checked={featured} onCheckedChange={setFeatured} />
        <Label htmlFor="featured">Öne Çıkan</Label>
      </div>

      {/* order */}
      <div className="space-y-1.5">
        <Label htmlFor="order">Sıra</Label>
        <Input
          id="order"
          type="number"
          min={0}
          value={order}
          onChange={(e) => setOrder(Number(e.target.value))}
          className="w-32"
        />
      </div>
    </form>
  );

  const mediaPanel = (
    <div className="space-y-4 pt-4">
      <div className="space-y-1.5">
        <Label>Logo URL</Label>
        <div className="flex gap-2">
          <Input
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://..."
            className="flex-1"
          />
          <Button type="button" variant="outline" onClick={() => setMediaOpen(true)}>
            Medyadan Seç
          </Button>
          {logoUrl && (
            <Button type="button" variant="destructive" onClick={() => setLogoUrl("")}>
              Kaldır
            </Button>
          )}
        </div>
      </div>
      {logoUrl && (
        <img
          src={logoUrl}
          alt="Logo önizleme"
          className="max-h-32 max-w-48 object-contain rounded border"
        />
      )}

      {/* Brand documents / PDF catalogs */}
      <div className="pt-2 border-t">
        <BrandDocumentsField value={documents} onChange={setDocuments} />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => confirmNavigate("/brands")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold">{isEdit ? "Marka Düzenle" : "Yeni Marka"}</h1>
      </div>

      <EntityTabs
        value={tab}
        onValueChange={setTab}
        seoDisabled={!isEdit}
        locale={lang}
        seoPath={seoPath}
        previewPath={previewPath}
        content={contentPanel}
        media={mediaPanel}
      />

      {/* Submit bar */}
      <div className="flex gap-2 pt-2">
        <Button form="brand-form" type="submit" disabled={isPending}>
          {isPending ? "Kaydediliyor…" : isEdit ? "Güncelle" : "Oluştur"}
        </Button>
        <Button variant="outline" onClick={() => confirmNavigate("/brands")}>
          İptal
        </Button>
      </div>

      <MediaPicker
        defaultFolder="brands"
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onSelect={(url) => {
          setLogoUrl(url);
          setMediaOpen(false);
        }}
      />
      {unsavedDialog}
    </div>
  );
}
